import { CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { TIPOS_PERIGO } from '../constants';
import { processarTextoColado, processarTextoMultiploItens } from '../utils/text-processor';
import { adicionarMultiplosItensLista } from '../utils/lista-manager';
import { obterPerigosEspecificos, traduzirTextoPerigo } from '../../constants/perigos-especificos';
import { rotuloTipoPerigo as rotuloTipoPerigoCompartilhado } from '../../constants/rotulos-dominio';
import { PARTES_CORPO_ATINGIDAS } from '../../constants/partes-corpo';
import { useI18n } from '@/contexts/i18n-hook';

interface TiposPerigoSectionProps {
  tiposPerigoSelecionados: string[];
  eventoPerigoso: string;
  conforme: boolean;
  perigosEncontrados: string[];
  potenciaisConsequencias: string[];
  potenciaisConsequenciasEspecificas?: string[];
  origemEspecifica?: string[];
  partesCorpoAtingidas?: string;
  setTiposPerigoSelecionados: (tipos: string[]) => void;
  setEventoPerigoso: (evento: string) => void;
  setConforme: (conforme: boolean) => void;
  setPerigosEncontrados: (perigos: string[]) => void;
  setPotenciaisConsequencias: (consequencias: string[]) => void;
  setPotenciaisConsequenciasEspecificas?: (consequencias: string[]) => void;
  setOrigemEspecifica?: (origem: string[]) => void;
  setPartesCorpoAtingidas?: (partes: string) => void;
}

export function TiposPerigoSection({
  tiposPerigoSelecionados,
  eventoPerigoso,
  conforme,
  perigosEncontrados,
  potenciaisConsequencias,
  partesCorpoAtingidas = 'Corpo inteiro',
  setTiposPerigoSelecionados,
  setEventoPerigoso,
  setConforme,
  setPerigosEncontrados,
  setPotenciaisConsequencias,
  setPartesCorpoAtingidas,
}: TiposPerigoSectionProps) {
  const { t } = useI18n();
  // Obter o tipo de perigo selecionado (apenas 1)
  const tipoPerigoSelecionado = tiposPerigoSelecionados.length > 0 ? tiposPerigoSelecionados[0] : '';

  // O rótulo mora em constants/rotulos-dominio para a tabela de riscos usar o
  // MESMO mapa — o valor salvo continua em português, só a etiqueta muda.
  const rotuloTipoPerigo = (tipo: string) => rotuloTipoPerigoCompartilhado(t, tipo);

  // Obter perigos específicos do tipo selecionado
  const perigosEspecificos = tipoPerigoSelecionado
    ? obterPerigosEspecificos(tipoPerigoSelecionado)
    : null;

  const handleTipoButtonClick = (tipo: string) => {
    // Permitir apenas 1 tipo selecionado
    const isDesmarcando = tiposPerigoSelecionados.includes(tipo);

    if (isDesmarcando) {
      setTiposPerigoSelecionados([]);
    } else {
      setTiposPerigoSelecionados([tipo]);
    }

    // Sempre limpar seleções específicas quando o tipo mudar ou for removido manualmente
    // Isso evita que perigos de um tipo permaneçam quando trocamos para outro
    setPerigosEncontrados([]);
    setPotenciaisConsequencias([]);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-0.5">{t('apreciacaoModais.editarParte.tiposPerigo.titulo')}</h2>

      <div className="mb-1">
        <label className="block text-sm font-medium text-gray-700 mb-0.5">
          {t('apreciacaoModais.editarParte.tiposPerigo.selecioneRotulo')}
        </label>
        <div className="flex flex-wrap gap-0.5">
          {TIPOS_PERIGO.map((tipo) => (
            <button
              key={tipo}
              type="button"
              onClick={() => handleTipoButtonClick(tipo)}
              className={`px-1.5 py-0.5 rounded text-sm font-medium ${tiposPerigoSelecionados.includes(tipo)
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {rotuloTipoPerigo(tipo)}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-1">
        <label className="block text-sm font-medium text-gray-700 mb-0.5">
          {t('apreciacaoModais.editarParte.tiposPerigo.eventoRotulo')}
        </label>
        <textarea
          value={eventoPerigoso}
          onChange={(e) => setEventoPerigoso(e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            const textoColado = e.clipboardData.getData('text');
            const textoProcessado = processarTextoColado(textoColado);
            setEventoPerigoso(textoProcessado);
          }}
          placeholder={t('apreciacaoModais.editarParte.tiposPerigo.eventoPlaceholder')}
          className="w-full border border-gray-300 rounded px-1.5 py-0.5"
          rows={3}
        />
      </div>

      <div className="mb-1">
        <label className="block text-sm font-medium text-gray-700 mb-0.5">
          {t('apreciacaoModais.editarParte.tiposPerigo.conformidadeRotulo')}
        </label>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setConforme(true)}
            className={`flex items-center px-1.5 py-0.5 rounded ${conforme
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-0.5" />
            {t('apreciacaoModais.editarParte.tiposPerigo.conforme')}
          </button>
          <button
            type="button"
            onClick={() => setConforme(false)}
            className={`flex items-center px-1.5 py-0.5 rounded ${!conforme
              ? 'bg-red-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <XCircle className="h-3.5 w-3.5 mr-0.5" />
            {t('apreciacaoModais.editarParte.tiposPerigo.naoConforme')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-0.5">
            {tipoPerigoSelecionado
              ? t('apreciacaoModais.editarParte.tiposPerigo.outrosPerigosComTipo', { tipo: rotuloTipoPerigo(tipoPerigoSelecionado) })
              : t('apreciacaoModais.editarParte.tiposPerigo.outrosPerigos')}
          </label>
          {perigosEspecificos ? (
            <div className="border border-gray-300 rounded">
              <div className="p-0.5 max-h-96 overflow-y-auto">
                {perigosEspecificos.origem.map((origem) => {
                  const isSelected = perigosEncontrados.includes(origem);
                  return (
                    <label
                      key={origem}
                      className="flex items-start mb-0.5 p-0.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (!perigosEncontrados.includes(origem)) {
                              setPerigosEncontrados([...perigosEncontrados, origem]);
                            }
                          } else {
                            setPerigosEncontrados(perigosEncontrados.filter(p => p !== origem));
                          }
                        }}
                        className="mt-0.5 mr-0.5"
                      />
                      <span className="text-sm text-gray-700 flex-1">{traduzirTextoPerigo(t, origem)}</span>
                    </label>
                  );
                })}
                {/* Itens personalizados adicionados */}
                {perigosEncontrados
                  .filter(perigo => !perigosEspecificos.origem.includes(perigo))
                  .map((perigo, index) => (
                    <div key={`custom-${index}`} className="flex items-center justify-between mb-0.5 p-0.5 bg-blue-50 rounded">
                      <span className="text-sm text-gray-700 flex-1">{perigo}</span>
                      <button
                        type="button"
                        onClick={() => setPerigosEncontrados(perigosEncontrados.filter(p => p !== perigo))}
                        className="text-red-600 hover:text-red-800 ml-0.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
              </div>
              <div className="border-t border-gray-300 p-0.5">
                <input
                  type="text"
                  placeholder={t('apreciacaoModais.editarParte.tiposPerigo.adicionarPerigo')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const valor = e.currentTarget.value.trim();
                      if (valor && !perigosEncontrados.includes(valor)) {
                        setPerigosEncontrados([...perigosEncontrados, valor]);
                        e.currentTarget.value = '';
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const textoColado = e.clipboardData.getData('text');
                    const itensProcessados = processarTextoMultiploItens(textoColado);
                    adicionarMultiplosItensLista(perigosEncontrados, setPerigosEncontrados, itensProcessados);
                    e.currentTarget.value = '';
                  }}
                  className="w-full border border-gray-300 rounded px-1.5 py-0.5 text-sm"
                />
              </div>
            </div>
          ) : (
            <div className="border border-gray-300 rounded p-0.5 min-h-[150px]">
              <p className="text-sm text-gray-500 italic">
                {t('apreciacaoModais.editarParte.tiposPerigo.semTipoPerigos')}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-0.5">
            {tipoPerigoSelecionado
              ? t('apreciacaoModais.editarParte.tiposPerigo.consequenciasComTipo', { tipo: rotuloTipoPerigo(tipoPerigoSelecionado) })
              : t('apreciacaoModais.editarParte.tiposPerigo.consequencias')}
          </label>
          {perigosEspecificos ? (
            <div className="border border-gray-300 rounded">
              <div className="p-0.5 max-h-96 overflow-y-auto">
                {perigosEspecificos.potenciaisConsequencias.map((consequencia) => {
                  const isSelected = potenciaisConsequencias.includes(consequencia);
                  return (
                    <label
                      key={consequencia}
                      className="flex items-start mb-0.5 p-0.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (!potenciaisConsequencias.includes(consequencia)) {
                              setPotenciaisConsequencias([...potenciaisConsequencias, consequencia]);
                            }
                          } else {
                            setPotenciaisConsequencias(potenciaisConsequencias.filter(c => c !== consequencia));
                          }
                        }}
                        className="mt-0.5 mr-0.5"
                      />
                      <span className="text-sm text-gray-700 flex-1">{traduzirTextoPerigo(t, consequencia)}</span>
                    </label>
                  );
                })}
                {/* Itens personalizados adicionados */}
                {potenciaisConsequencias
                  .filter(consequencia => !perigosEspecificos.potenciaisConsequencias.includes(consequencia))
                  .map((consequencia, index) => (
                    <div key={`custom-${index}`} className="flex items-center justify-between mb-0.5 p-0.5 bg-blue-50 rounded">
                      <span className="text-sm text-gray-700 flex-1">{traduzirTextoPerigo(t, consequencia)}</span>
                      <button
                        type="button"
                        onClick={() => setPotenciaisConsequencias(potenciaisConsequencias.filter(c => c !== consequencia))}
                        className="text-red-600 hover:text-red-800 ml-0.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
              </div>
              <div className="border-t border-gray-300 p-0.5">
                <input
                  type="text"
                  placeholder={t('apreciacaoModais.editarParte.tiposPerigo.adicionarConsequencia')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const valor = e.currentTarget.value.trim();
                      if (valor && !potenciaisConsequencias.includes(valor)) {
                        setPotenciaisConsequencias([...potenciaisConsequencias, valor]);
                        e.currentTarget.value = '';
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const textoColado = e.clipboardData.getData('text');
                    const itensProcessados = processarTextoMultiploItens(textoColado);
                    adicionarMultiplosItensLista(potenciaisConsequencias, setPotenciaisConsequencias, itensProcessados);
                    e.currentTarget.value = '';
                  }}
                  className="w-full border border-gray-300 rounded px-1.5 py-0.5 text-sm"
                />
              </div>
            </div>
          ) : (
            <div className="border border-gray-300 rounded p-0.5 min-h-[150px]">
              <p className="text-sm text-gray-500 italic">
                {t('apreciacaoModais.editarParte.tiposPerigo.semTipoConsequencias')}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mb-1">
        <label className="block text-sm font-medium text-gray-700 mb-0.5">
          {t('apreciacaoModais.editarParte.tiposPerigo.partesCorpoRotulo')}
        </label>
        <select
          value={partesCorpoAtingidas}
          onChange={(e) => setPartesCorpoAtingidas?.(e.target.value)}
          className="w-full border border-gray-300 rounded px-1.5 py-0.5 text-sm"
        >
          {PARTES_CORPO_ATINGIDAS.map((parte) => (
            <option key={parte} value={parte}>
              {parte}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

